import { ErroRequisicao } from './api';

/**
 * Fase 2 (W16, pendência da W3 e review r16 robustez M6): recuo e agrupamento para as consultas de fundo (sino, contador,
 * central). Uma rajada de 800 avisos virava 2 GETs por sinal; com o balde de 600/min por usuário, a aba entrava em 429 e
 * o usuário passava a receber 429 em tudo. Aqui:
 * - `Recuo`: depois de um 429 ou 503, ninguém pergunta de novo antes da espera (exponencial, ou o `Retry-After` do servidor,
 *   o que for maior); o sucesso zera;
 * - `criarAgrupador`: os sinais de uma rajada viram uma recarga só (espera curta, com teto), nada roda com a aba oculta (fica
 *   pendente e roda uma vez ao voltar) e nada roda durante o recuo (roda uma vez quando ele acaba).
 */

/** Sobrecarga do servidor: 429 (limite) ou 503 (indisponível). Outros erros não entram no recuo. */
export const ehSobrecarga = (e: unknown): e is ErroRequisicao => e instanceof ErroRequisicao && (e.status === 429 || e.status === 503);

/** A consulta nem saiu: o recuo ainda está correndo. Não conta como falha nova, não tenta de novo e não mostra erro. */
export class ErroEmRecuo extends Error {
  constructor(public readonly esperaMs: number) {
    super('recuo');
    this.name = 'ErroEmRecuo';
  }
}

export interface OpcoesRecuo {
  /** Primeira espera (ms); dobra a cada falha seguida. */
  base?: number;
  /** Teto da espera exponencial (ms). O `Retry-After` maior que o teto ainda vale, até `tetoServidor`. */
  teto?: number;
  /** Maior espera aceita do servidor (ms), para um cabeçalho absurdo não calar o sino por horas. */
  tetoServidor?: number;
  agora?: () => number;
  /** Fração aleatória somada à espera (0 a 0,2), para abas abertas juntas não voltarem no mesmo instante. */
  acaso?: () => number;
}

export class Recuo {
  private falhas = 0;
  private liberaEm = 0;
  private readonly base: number;
  private readonly teto: number;
  private readonly tetoServidor: number;
  private readonly agora: () => number;
  private readonly acaso: () => number;

  constructor(o: OpcoesRecuo = {}) {
    this.base = o.base ?? 5_000;
    this.teto = o.teto ?? 300_000;
    this.tetoServidor = o.tetoServidor ?? 900_000;
    this.agora = o.agora ?? Date.now;
    this.acaso = o.acaso ?? Math.random;
  }

  /** Anota a falha. Devolve true quando era sobrecarga (e o recuo passou a valer). */
  falhou(e: unknown): boolean {
    if (!ehSobrecarga(e)) return false;
    this.falhas += 1;
    const exponencial = Math.min(this.teto, this.base * 2 ** (this.falhas - 1));
    const doServidor = e.esperaSegundos !== null ? Math.min(this.tetoServidor, e.esperaSegundos * 1000) : 0;
    const espera = Math.max(exponencial, doServidor);
    this.liberaEm = this.agora() + espera + Math.round(espera * 0.2 * Math.min(Math.max(this.acaso(), 0), 1));
    return true;
  }

  ok(): void {
    this.falhas = 0;
    this.liberaEm = 0;
  }

  bloqueado(): boolean {
    return this.agora() < this.liberaEm;
  }

  /** Quanto falta (ms) para a próxima tentativa; 0 fora do recuo. */
  restante(): number {
    return Math.max(0, this.liberaEm - this.agora());
  }

  /** Falhas de sobrecarga seguidas (para teste e diagnóstico). */
  get seguidas(): number {
    return this.falhas;
  }

  /** Roda a consulta respeitando o recuo: durante ele, recusa sem rede (`ErroEmRecuo`); depois, anota o resultado. */
  async executar<T>(fn: () => Promise<T>): Promise<T> {
    if (this.bloqueado()) throw new ErroEmRecuo(this.restante());
    try {
      const r = await fn();
      this.ok();
      return r;
    } catch (e) {
      this.falhou(e);
      throw e;
    }
  }
}

/** Repete a consulta? Nunca no recuo nem na sobrecarga (o recuo cuida); 4xx não; o resto até 2 vezes (regra do app). */
export function deveRepetir(n: number, e: unknown): boolean {
  if (e instanceof ErroEmRecuo || ehSobrecarga(e)) return false;
  return !(e instanceof ErroRequisicao && e.status < 500) && n < 2;
}

/** A aba está à vista? Fora do navegador (testes, SSR), sempre. */
export const abaVisivel = () => typeof document === 'undefined' || document.visibilityState !== 'hidden';

export interface OpcoesAgrupador {
  executar: () => void;
  /** Silêncio (ms) que fecha a rajada. */
  espera?: number;
  /** Teto (ms) desde o primeiro sinal: rajada sem fim ainda recarrega de tempos em tempos. */
  maximo?: number;
  /** Recuo que segura a recarga enquanto corre. */
  recuo?: Pick<Recuo, 'bloqueado' | 'restante'>;
  visivel?: () => boolean;
  /** Assina a volta da aba (padrão: `visibilitychange` do documento). Devolve a função que desassina. */
  aoMudarVisibilidade?: (fn: () => void) => () => void;
}

export interface Agrupador {
  /** Um sinal chegou. */
  sinal(): void;
  /** Para os relógios e solta a escuta da aba. */
  parar(): void;
  /** Há recarga esperando (aba oculta, recuo ou rajada em curso). */
  readonly pendente: boolean;
}

const escutarDocumento = (fn: () => void) => {
  if (typeof document === 'undefined') return () => {};
  document.addEventListener('visibilitychange', fn);
  return () => document.removeEventListener('visibilitychange', fn);
};

/** Junta os sinais de uma rajada numa recarga só, sem rodar com a aba oculta nem durante o recuo. */
export function criarAgrupador(o: OpcoesAgrupador): Agrupador {
  const espera = o.espera ?? 800;
  const maximo = o.maximo ?? 3_000;
  const visivel = o.visivel ?? abaVisivel;
  let pendente = false;
  let primeiro = 0;
  let relogio: ReturnType<typeof setTimeout> | null = null;

  const limpar = () => {
    if (relogio) clearTimeout(relogio);
    relogio = null;
  };
  const agendar = (ms: number) => {
    limpar();
    relogio = setTimeout(disparar, Math.max(0, ms));
  };
  function disparar() {
    relogio = null;
    if (!pendente) return;
    if (!visivel()) return; // fica pendente: roda uma vez quando a aba voltar
    if (o.recuo?.bloqueado()) return agendar(o.recuo.restante() + 50);
    pendente = false;
    primeiro = 0;
    o.executar();
  }
  const soltar = (o.aoMudarVisibilidade ?? escutarDocumento)(() => {
    if (pendente && visivel()) agendar(0);
  });

  return {
    sinal() {
      const agora = Date.now();
      if (!pendente) primeiro = agora;
      pendente = true;
      if (!visivel()) return limpar();
      agendar(Math.min(espera, primeiro + maximo - agora));
    },
    parar() {
      limpar();
      soltar();
      pendente = false;
    },
    get pendente() {
      return pendente;
    },
  };
}
